
import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import heartLoading from "../../../assests/heartLoading.gif";


const parseGraphData = (graphData) => {
  if (!graphData) return [];
  const keys = Object.keys(graphData);
  if (!keys.length) return [];
  const dateMap = {};
  const totalKeys = keys.filter((k) => k.startsWith("x")).length;
  for (let i = 1; i <= totalKeys; i++) {
    const date = graphData[`x${i}`];
    const value = graphData[`y${i}`] ?? 0;
    if (!date) continue;
    dateMap[date] = (dateMap[date] || 0) + value;
  }
  return Object.entries(dateMap)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
};

const formatXLabel = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatTooltipDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const ACCENT = "#7e22ce";       // purple-700
const ACCENT_LIGHT = "#a855f7"; // purple-500 (lighter shade for peak bar)
const TOOLTIP_COLOR = "#2dd4bf"; // teal-400

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1.5px solid #e5e7eb",
        borderRadius: 10,
        padding: "10px 16px",
        boxShadow: "0 4px 20px rgba(45,212,191,0.15)",
        minWidth: 160,
      }}
    >
      <p style={{ color: "#6b7280", fontSize: 12, marginBottom: 4 }}>
        {formatTooltipDate(label)}
      </p>
      <p style={{ color: TOOLTIP_COLOR, fontWeight: 700, fontSize: 20, margin: 0 }}>
        {payload[0].value.toLocaleString()}
        <span style={{ fontSize: 13, fontWeight: 400, color: "#6b7280", marginLeft: 5 }}>
          queries
        </span>
      </p>
    </div>
  );
};

export const UsageTrend = ({ graphData, loading }) => {
  const allData = useMemo(() => parseGraphData(graphData || {}), [graphData]);

  const maxCount = useMemo(
    () => Math.max(...allData.map((d) => d.count), 1),
    [allData]
  );

  const totalQueries = useMemo(
    () => allData.reduce((sum, d) => sum + d.count, 0),
    [allData]
  );

  const avgQueries = allData.length
    ? (totalQueries / allData.length).toFixed(1)
    : 0;

  const peakDay = useMemo(
    () => allData.reduce((max, d) => (d.count > (max?.count ?? 0) ? d : max), null),
    [allData]
  );

  if (loading) {
    return (
     <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <img
            src={heartLoading}
            alt="Loading..."
            className="h-30"
          />
          <p className="text-gray-700 mt-[15px] text-[20px] text-center">        Loading usage trend...
</p>
        </div>
      </div>
    );
  }

  if (!graphData || Object.keys(graphData).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2 text-gray-400">
        <span className="text-sm">No usage data available for this asset.</span>
      </div>
    );
  }

  const labelInterval = Math.max(1, Math.floor(allData.length / 10));

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">Usage Trend</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Query volume over time for this data asset
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: "Total Queries", value: totalQueries.toLocaleString() },
          { label: "Daily Avg", value: avgQueries },
          {
            label: "Peak Day",
            value: peakDay
              ? `${peakDay.count.toLocaleString()} on ${formatXLabel(peakDay.date)}`
              : "—",
          },
        ].map((kpi) => (
          <div
            key={kpi.label}
            className="bg-[#E6F1F9] rounded-xl px-4 py-3 border border-blue-100"
          >
            <p className="text-xs text-gray-500 mb-0.5">{kpi.label}</p>
            <p className="text-base font-bold text-[#bb1ccc] truncate">{kpi.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4" style={{ minHeight: 320 }}>
        {allData.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
            No data available.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={allData}
              margin={{ top: 10, right: 10, left: 0, bottom: 30 }}
              barCategoryGap="30%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatXLabel}
                interval={labelInterval - 1}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
                angle={-35}
                textAnchor="end"
                height={50}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                width={45}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f5f0ff" }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={40}>
                {allData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.count === maxCount ? ACCENT_LIGHT : ACCENT}
                    fillOpacity={0.6 + 0.4 * (entry.count / maxCount)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

    
    </div>
  );
};